/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {
  EntityStatus,
  GenericObject,
  Schedule,
  ScheduleConfiguration
} from '@ion/core/src/types/entities';
import {FormState} from '../hooks/useFormState';
import {convertStatus, dateTimeFormatter} from '../utils/ComponentUtils';

// schedule fields included in calls to the CTC API ...
export enum ScheduleFields {
  Id = 'id',
  Version = 'version',
  Name = 'name',
  Account = 'account',
  Description = 'description',
  Status = 'status',
  Timezone = 'timeZone',
  Inheritable = 'inheritable',
  Configs = 'scheduleConfigs'
}

export enum ScheduleConfigFields {
  Day = 'day',
  Time = 'time',
  StartTime = 'startTime',
  EndTime = 'endTime'
}

// available field names for the add/edit schedule form ...
export enum ScheduleFormFields {
  Name = 'name',
  Group = 'group',
  Description = 'description',
  Status = 'status',
  Timezone = 'timezone',
  Inheritable = 'inheritable',
  CreatedOn = 'createdOn',
  LastModifiedOn = 'lastModifiedOn',
  Configs = 'scheduleConfigs'
}

export enum ScheduleConfigFormFields {
  Day = 'day',
  StartTime = 'startTime',
  EndTime = 'endTime'
}

// defines the schedule event target type definition ...
export interface TargetType {
  name: ScheduleFormFields | ScheduleConfigFormFields;
  value: unknown;
  required?: boolean;
}

// default schedule configuration values
export const scheduleConfigDefaultValue = {
  [ScheduleConfigFields.Day] : null
};
export const scheduleConfigsDefaultValue = [scheduleConfigDefaultValue];

// initial form state values
const required    = {required: true};
const notRequired = {required: false};

const scheduleConfigInitialState = {
  [ScheduleConfigFormFields.Day]       : required,
  [ScheduleConfigFormFields.StartTime] : notRequired,
  [ScheduleConfigFormFields.EndTime]   : notRequired
};
const scheduleConfigsInitialState = {
  ...required,
  value : scheduleConfigsDefaultValue
};

/**
 * Provides the initial state of the schedule editor form, this state includes all named
 * editor fields and their requirements, as well as any initial values required, or a
 * reference to a schedule, in the case of edits.
 */
export const getInitialState = ({schedule}: GenericObject) => {
  const configs = schedule?.[ScheduleFields.Configs];
  return deserialize(schedule) || {
    [ScheduleFormFields.Name]           : required,
    [ScheduleFormFields.Group]          : required,
    [ScheduleFormFields.Description]    : notRequired,
    [ScheduleFormFields.Status]         : {...notRequired, value: EntityStatus.Enabled},
    [ScheduleFormFields.Timezone]       : notRequired,
    [ScheduleFormFields.Inheritable]    : {...notRequired, value: true},
    [ScheduleFormFields.CreatedOn]      : notRequired,
    [ScheduleFormFields.LastModifiedOn] : notRequired,
    [ScheduleFormFields.Configs]        : getConfigsInitialState({configs})
  };
};

export const getConfigsInitialState = ({configs}: GenericObject) => {
  return deserializeConfigs(configs) || scheduleConfigsInitialState;
};

export const getConfigInitialState = ({config}: GenericObject) => {
  return deserializeConfig(config) || scheduleConfigInitialState;
};

/**
 * Removes properties on the specified object that are not valid for a CTC
 * Schedule Entity. The object returned should be valid to specify in calls
 * to the CTC Schedules API.
 */
export const sanitize = (schedule: GenericObject): Schedule => {
  const valid: string[] = Object.values(ScheduleFields);
  const updated: Schedule = {};
  for (let [key, value] of Object.entries(schedule)) {
    if (valid.includes(key)) {
      if (key === ScheduleFields.Configs) {
        value = sanitizeConfigs(value);
      }
      updated[key] = value;
    }
  }
  return updated;
};

export const sanitizeConfigs = (configs: GenericObject[]): ScheduleConfiguration[] => {
  return configs.map(config => sanitizeConfig(config));
};

export const sanitizeConfig = (config: GenericObject): ScheduleConfiguration => {
  const valid: string[] = Object.values(ScheduleConfigFields);
  const updated: ScheduleConfiguration = {};
  for (const [key, value] of Object.entries(config)) {
    if (valid.includes(key)) {
      if (key === ScheduleConfigFields.Time) {
        if (value?.length > 0) {
          for (const [timeKey, timeValue] of Object.entries(value[0])) {
            if (valid.includes(timeKey)) {
              if (!updated[key]) {
                updated[key] = [{}];
              }
              updated[key][0][timeKey] = timeValue;
            }
          }
        }
      } else {
        updated[key] = value;
      }
    }
  }
  return updated;
};

/**
 * @serialize
 *
 * Serializes the Schedule Editor Form Fields to their corresponding attributes required for
 * providing a valid CTC Schedule entity representation.
 *
 * @param   {FormState} state The Schedule Editor Form state object which contains all current
 *          form values.
 * @returns {Schedule} A serialized, valid CTC Schedule entity representation for subsequent
 *          persistence to the CTC platform.
 */
export const serialize = (state: FormState): Schedule => {
  const {schedule, ...rest} = state;
  const serialized: GenericObject = {...schedule};
  for (const [key, {value}] of Object.entries(rest)) {
    switch (key) {
      case ScheduleFormFields.Group :
        serialized[ScheduleFields.Account] = value;
        break;
      case ScheduleFormFields.Timezone :
        serialized[ScheduleFields.Timezone] = value;
        break;
      case ScheduleFormFields.Status :
        serialized[ScheduleFormFields.Status] = convertStatus(value);
        break;
      default :
        if (value || value === false) {
          serialized[key] = value;
        }
        break;
    }
  }
  return sanitize(serialized);
};

/**
 * @serializeConfig
 *
 * Serializes the Schedule Configuration Editor Form Fields to their corresponding attributes
 * required for providing a valid CTC Schedule entity representation.
 *
 * @param   {FormState} state The Schedule Configuration Editor Form state object which
 *          contains all current form values.
 * @returns {ScheduleConfiguration} A serialized, valid CTC Schedule Configuration entity
 *          representation for subsequent persistence to the CTC platform.
 */
export const serializeConfig = (state: FormState): ScheduleConfiguration => {
  const {config, ...rest} = state;
  const serialized: GenericObject = {...config};
  for (const [key, {value}] of Object.entries(rest)) {
    switch (key) {
      default :
        if (value || value === false) {
          serialized[key] = value;
        }
        break;
    }
  }
  return sanitizeConfig(serialized);
};

/**
 * @deserialize
 *
 * Deserializes a CTC Schedule into an object which can be consumed by the Schedule Editor Form
 * Fields and used to pre-populated their respective values.
 *
 * @param {Schedule} schedule An optional CTC Schedule instance that is to be deserialized for
 *        the form.
 */
export const deserialize = (schedule: Schedule) => {
  if (schedule) {
    return {
      schedule,
      [ScheduleFormFields.Name] : {
        name     : ScheduleFormFields.Name,
        value    : schedule[ScheduleFields.Name],
        required : true
      },
      [ScheduleFormFields.Group] : {
        name     : ScheduleFormFields.Group,
        value    : schedule[ScheduleFields.Account],
        required : true
      },
      [ScheduleFormFields.Description] : {
        name     : ScheduleFormFields.Description,
        value    : schedule[ScheduleFields.Description],
        required : false
      },
      [ScheduleFormFields.Status] : {
        name     : ScheduleFormFields.Status,
        value    : schedule[ScheduleFields.Status],
        required : false
      },
      [ScheduleFormFields.Timezone] : {
        name     : ScheduleFormFields.Timezone,
        value    : schedule[ScheduleFields.Timezone],
        required : false
      },
      [ScheduleFormFields.Inheritable] : {
        name     : ScheduleFormFields.Inheritable,
        value    : schedule[ScheduleFields.Inheritable],
        required : false
      },
      [ScheduleFormFields.CreatedOn] : {
        name     : ScheduleFormFields.CreatedOn,
        value    : dateTimeFormatter(schedule.createdOn),
        required : false
      },
      [ScheduleFormFields.LastModifiedOn] : {
        name     : ScheduleFormFields.LastModifiedOn,
        value    : dateTimeFormatter(schedule.lastModifiedOn),
        required : false
      },
      [ScheduleFormFields.Configs] : {
        name     : ScheduleFormFields.Configs,
        value    : schedule[ScheduleFields.Configs],
        required : true
      }
    };
  }
};

/**
 * @deserializeConfigs
 *
 * Deserializes a CTC Schedule's configuration list into an object which can be consumed by
 * the Schedule Editor Form Fields and used to pre-populated their respective values.
 *
 * @param {ScheduleConfiguration[]} configs An optional CTC Schedule configuration list
 *                                  instance that is to be deserialized for the form.
 */
export const deserializeConfigs = (configs: ScheduleConfiguration[]) => {
  return configs?.map((config) => deserializeConfig(config));
};

/**
 * @deserializeConfig
 *
 * Deserializes a CTC Schedule configuration into an object which can be consumed by
 * the Schedule Editor Form Fields and used to pre-populated their respective values.
 *
 * @param {ScheduleConfiguration} config An optional CTC Schedule configuration instance
 *                                that is to be deserialized for the form.
 */
export const deserializeConfig = (config: ScheduleConfiguration) => {
  if (config) {
    return {
      config,
      [ScheduleConfigFormFields.Day] : {
        name     : ScheduleConfigFormFields.Day,
        value    : config[ScheduleConfigFields.Day],
        required : true
      },
      [ScheduleConfigFormFields.StartTime] : {
        name     : ScheduleConfigFormFields.StartTime,
        value    : config[ScheduleConfigFields.Time]?.[0]?.[ScheduleConfigFields.StartTime],
        required : false
      },
      [ScheduleConfigFormFields.EndTime] : {
        name     : ScheduleConfigFormFields.EndTime,
        value    : config[ScheduleConfigFields.Time]?.[0]?.[ScheduleConfigFields.EndTime],
        required : false
      }
    };
  }
};

/**
 * @validateConfigs
 *
 * Validates a CTC Schedule's configuration list.
 *
 * @param {ScheduleConfiguration[]} configs An optional CTC Schedule configuration list
 *                                  instance that is to be validated.
 */
export const validateConfigs = (configs: ScheduleConfiguration[]): boolean => {
  return configs?.map((config) => validateConfig(config)).every((v) => v);
};

/**
 * @validateConfig
 *
 * Validates a CTC Schedule configuration.
 *
 * @param {ScheduleConfiguration} config An optional CTC Schedule configuration instance
 *                                that is to be validated.
 */
export const validateConfig = (config: ScheduleConfiguration): boolean => {
  const day       = config?.[ScheduleConfigFields.Day];
  const time      = config?.[ScheduleConfigFields.Time]?.[0];
  const startTime = time?.[ScheduleConfigFields.StartTime];
  const endTime   = time?.[ScheduleConfigFields.EndTime];
  return day &&
    ((!startTime && !endTime) ||
      (startTime && endTime && startTime < endTime));
};
